import { FileText, ShoppingCart, CheckCircle2, ArrowRight, Image as ImageIcon } from "lucide-react";
import { PROJECTS } from "@/data/content";
import { Reveal } from "./Reveal";

const ApiIcon = () => (
  <div className="relative grid h-16 w-16 shrink-0 place-items-center rounded-2xl border-2 border-blue-600 bg-white font-display text-[19px] font-extrabold text-blue-600">
    API
    <span className="absolute -bottom-2 left-3 h-3 w-3 rounded-full border-2 border-blue-600 bg-white" />
    <span className="absolute -bottom-2 right-3 h-3 w-3 rounded-full border-2 border-blue-600 bg-white" />
  </div>
);

const Ok = () => <CheckCircle2 className="h-6 w-6 shrink-0 fill-emerald-500 text-white" />;

const VISUALS = {
  api: (
    <div className="flex items-end gap-3">
      <div className="h-[72px] flex-1 overflow-hidden whitespace-nowrap rounded-md bg-[#0F1A2E] p-2 font-mono text-[9px] leading-[1.5] text-sky-300">
        <div>$ run checks</div><div className="text-emerald-400">✓ 42 passed</div><div className="text-sky-300">! 1 anomaly</div><div className="text-slate-400">+ test 404</div>
      </div>
      <div className="relative h-[72px] flex-1 rounded-md border border-slate-200 bg-white p-2 font-mono text-[9px] leading-[1.5] text-blue-600">
        <div>{'{"status": 200,'}</div><div className="text-slate-500">{'"latency": 118,'}</div><div>{'"schema": "ok"}'}</div>
        <div className="absolute bottom-1.5 right-1.5"><Ok /></div>
      </div>
    </div>
  ),
  doc: (
    <div className="flex items-center justify-between gap-2">
      <FileText className="h-11 w-11 text-blue-700" strokeWidth={1.5} />
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] shadow-sm">
        <b className="text-[#0B1B33]">PDF</b><ArrowRight className="h-3.5 w-3.5" /><span className="text-slate-700">Insights</span>
        <ImageIcon className="ml-1 h-5 w-5 text-blue-600" />
      </div>
      <Ok />
    </div>
  ),
  retail: (
    <div className="flex items-end justify-between gap-3">
      <div className="flex-1 space-y-1.5">
        {["w-4/5 bg-blue-500", "w-3/5 bg-teal-400", "w-11/12 bg-slate-300", "w-2/3 bg-sky-400"].map((c) => <div key={c} className={`h-1.5 rounded ${c}`} />)}
      </div>
      <div className="flex h-12 flex-1 items-end gap-1">
        {[30, 55, 40, 70, 50, 85, 65, 95].map((h, i) => <span key={i} className="flex-1 rounded-t bg-gradient-to-t from-blue-600 to-sky-400" style={{ height: `${h}%` }} />)}
      </div>
      <Ok />
    </div>
  ),
};

const ICONS = {
  api: <ApiIcon />,
  doc: <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-blue-50"><FileText className="h-10 w-10 text-blue-600" strokeWidth={1.4} /></div>,
  retail: <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-sky-50"><ShoppingCart className="h-10 w-10 text-sky-600" strokeWidth={1.4} /></div>,
};

export const Projects = () => (
  <section id="projects" data-testid="projects-section">
    <Reveal>
      <h2 className="font-display text-[24px] font-bold tracking-tight text-[#0B1B33]">
        Project examples <span className="font-body text-[14px] font-normal text-slate-600">(not guaranteed individual outcomes)</span>
      </h2>
    </Reveal>
    <div className="mt-4 grid gap-4 md:grid-cols-3">
      {PROJECTS.map((p, i) => (
        <Reveal key={p.key} delay={i * 0.1}>
          <article data-testid={`project-card-${i + 1}`} className="card-lift flex h-full flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex gap-4">
              {ICONS[p.key]}
              <div>
                <h3 className="font-display text-[16px] font-bold leading-tight text-[#0B1B33]">{p.title}</h3>
                <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[13.5px] text-slate-700">
                  {p.bullets.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </div>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">{VISUALS[p.key]}</div>
          </article>
        </Reveal>
      ))}
    </div>
    <p className="mt-3 text-center text-[13px] text-slate-600">Examples only; results vary.</p>
  </section>
);
